import { create } from 'zustand';
import {
  listDisplayNames, setDisplayName, clearDisplayName, type DisplayNameKind,
} from '../api/chat-display-names';

/**
 * 내가 부르는 이름 (chat-display-names).
 *
 * 방 목록·대화 머리·참여자·말풍선이 **모두 같은 이름**을 써야 해서 상태를 한 곳에 둔다.
 * 화면마다 따로 불러오면 한 곳만 옛 이름으로 남는 일이 생긴다.
 *
 * 서버가 아직 이 창구를 모르면(404) 조용히 빈 채로 둔다 — 이름은 대화를 막을 만한 것이 아니고,
 * 그러면 지금까지의 이름이 그대로 보인다(무회귀).
 */

interface ChatDisplayNameState {
  /** `${kind}:${targetId}` → 내가 지은 이름. */
  names: Record<string, string>;
  loaded: boolean;
  load: () => Promise<void>;
  /** 이름을 지정한다. 빈 문자열이면 되돌린다. */
  rename: (kind: DisplayNameKind, targetId: string, name: string) => Promise<void>;
  reset: () => void;
}

const keyOf = (kind: DisplayNameKind, targetId: string) => `${kind}:${targetId}`;

export const useChatDisplayNameStore = create<ChatDisplayNameState>((set, get) => ({
  names: {},
  loaded: false,

  load: async () => {
    if (get().loaded) return;
    try {
      const list = await listDisplayNames();
      const map: Record<string, string> = {};
      for (const e of list) map[keyOf(e.kind, e.targetId)] = e.name;
      set({ names: map, loaded: true });
    } catch {
      // 서버가 아직 모르거나 잠깐 실패 — 지금까지의 이름을 그대로 쓴다.
      set({ loaded: true });
    }
  },

  rename: async (kind, targetId, name) => {
    const trimmed = name.trim();
    if (trimmed) {
      await setDisplayName(kind, targetId, trimmed);
      set((s) => ({ names: { ...s.names, [keyOf(kind, targetId)]: trimmed } }));
      return;
    }
    await clearDisplayName(kind, targetId);
    set((s) => {
      const next = { ...s.names };
      delete next[keyOf(kind, targetId)];
      return { names: next };
    });
  },

  reset: () => set({ names: {}, loaded: false }),
}));

/** 내가 지은 이름(없으면 null). 컴포넌트 밖에서도 쓸 수 있게 순수 함수로 둔다. */
export function myNameFor(
  names: Record<string, string>, kind: DisplayNameKind, targetId: string | null | undefined,
): string | null {
  if (!targetId) return null;
  return names[keyOf(kind, targetId)] ?? null;
}
